import { Badge, Group, SegmentedControl, Text } from '@mantine/core';
import { IconChartLine, IconFiles, IconHelpCircle, IconListCheck, IconMessages, IconSearch } from '@tabler/icons-react';
import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { dateToEndOfDay, dateToStartOfDay, daysAgoISODate, todayISODate } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';
import DocumentDetailScreen from './DocumentDetailScreen';
import DocumentsScreen from './DocumentsScreen';
import EvaluationsScreen from './EvaluationsScreen';
import IngestQueueScreen from './IngestQueueScreen';
import QaReviewScreen from './QaReviewScreen';
import SuperAdminBadge from '../SuperAdmin/SuperAdminBadge';
import RetrievalDebuggerScreen from './RetrievalDebuggerScreen';
import UnansweredScreen, { UNANSWERED_DEFAULT_DAYS } from './UnansweredScreen';

// Two mounts, no toggle: corpus="domain" inside Chat Bot settings manages the
// merchant's own knowledge (the shopper agent), corpus="system" inside the Super
// Admin panel manages Releva product documentation, served only to Lucy, the admin
// assistant (not to the shopper agent). Evaluations and the Retrieval Debugger
// render on both mounts, but only for superadmins — the domain mount is where a
// client-corpus run gets a real domainId to run against.
const KnowledgeBaseTab = ({ isSuperAdmin, corpus = 'domain' }) => {
  const { accountId, domainId } = useParams();
  const domain = useSelector((state) => state.domain.domain);
  const fetch = useFetch();

  const [screen, setScreen] = useState('documents');
  const [detailDocId, setDetailDocId] = useState(null);
  const [counts, setCounts] = useState({ documents: null, pendingQa: null, unanswered: null });

  const apiBase =
    corpus === 'system'
      ? '/api/admin/knowledge/system'
      : `/api/admin/accounts/${accountId}/domains/${domainId}/knowledge`;
  const defaultLocale = corpus === 'system' ? 'en' : domain?.locale;

  const refreshCounts = useCallback(() => {
    fetch(`${apiBase}/documents?perPage=1`)
      .then(({ total }) => setCounts((prev) => ({ ...prev, documents: total })))
      .catch(() => {});
    fetch(`${apiBase}/qa?reviewStatus=draft&synthetic=true&perPage=1`)
      .then(({ total }) => setCounts((prev) => ({ ...prev, pendingQa: total })))
      .catch(() => {});
    if (corpus === 'domain') {
      // Same window UnansweredScreen opens on — an unwindowed count would be over the
      // backend default and could visibly disagree with the list's own total.
      const unansweredWindow = queryString.stringify({
        perPage: 1,
        from: dateToStartOfDay(daysAgoISODate(UNANSWERED_DEFAULT_DAYS - 1), 'local'),
        to: dateToEndOfDay(todayISODate(), 'local'),
      });
      fetch(`${apiBase}/unanswered?${unansweredWindow}`)
        .then(({ total }) => setCounts((prev) => ({ ...prev, unanswered: total })))
        .catch(() => {});
    }
  }, [apiBase, fetch, corpus]);

  // Once the screen is open its own total wins — it knows the picked range.
  const setUnansweredCount = useCallback(
    (total) => setCounts((prev) => (prev.unanswered === total ? prev : { ...prev, unanswered: total })),
    []
  );

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  const openDocument = (docId) => {
    setScreen('documents');
    setDetailDocId(docId);
  };

  const screenProps = {
    apiBase,
    scope: corpus,
    isSuperAdmin,
    defaultLocale,
    refreshCounts,
    onOpenDocument: openDocument,
  };

  return (
    <>
      {/* Second navigation level as a segmented view-switcher, not a second tab
          bar — two stacked tab rows read as one confused set of controls. */}
      <Group justify="space-between" align="center" mt="lg" mb="sm">
        <SegmentedControl
          value={screen}
          onChange={(value) => {
            setScreen(value);
            setDetailDocId(null);
          }}
          data={[
            {
              value: 'documents',
              icon: IconFiles,
              text: `Documents${counts.documents != null ? ` ${counts.documents}` : ''}`,
            },
            { value: 'qa', icon: IconMessages, text: `Q&A Review${counts.pendingQa ? ` ${counts.pendingQa}` : ''}` },
            ...(corpus === 'domain'
              ? [
                  {
                    value: 'unanswered',
                    icon: IconHelpCircle,
                    text: `Unanswered${counts.unanswered ? ` ${counts.unanswered}` : ''}`,
                  },
                ]
              : []),
            { value: 'queue', icon: IconListCheck, text: 'Ingest Queue' },
            ...(isSuperAdmin
              ? [
                  { value: 'debugger', icon: IconSearch, text: 'Retrieval Debugger', superAdmin: true },
                  { value: 'evaluations', icon: IconChartLine, text: 'Evaluations', superAdmin: true },
                ]
              : []),
          ].map(({ value, icon: Icon, text, superAdmin }) => ({
            value,
            label: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon size={14} stroke={1.5} />
                {text}
                {superAdmin && <SuperAdminBadge show iconOnly />}
              </span>
            ),
          }))}
        />

        {corpus === 'system' && (
          <Group gap="xs">
            <Badge variant="light" color="warning" size="sm">
              system corpus
            </Badge>
            <Text size="xs" c="dimmed">
              Releva product documentation, served only to Lucy, the admin assistant (not to the shopper agent).
            </Text>
          </Group>
        )}
      </Group>

      {screen === 'documents' && detailDocId && (
        <DocumentDetailScreen {...screenProps} docId={detailDocId} onBack={() => setDetailDocId(null)} />
      )}
      {screen === 'documents' && !detailDocId && <DocumentsScreen {...screenProps} />}
      {screen === 'qa' && <QaReviewScreen {...screenProps} />}
      {screen === 'unanswered' && corpus === 'domain' && (
        <UnansweredScreen {...screenProps} onTotalChange={setUnansweredCount} />
      )}
      {screen === 'queue' && <IngestQueueScreen {...screenProps} />}
      {screen === 'debugger' && isSuperAdmin && <RetrievalDebuggerScreen {...screenProps} />}
      {screen === 'evaluations' && isSuperAdmin && <EvaluationsScreen {...screenProps} />}
    </>
  );
};

export default KnowledgeBaseTab;
